import { apply, onceToday } from '../core/effects';
import { S } from '../core/game';
import type { AffectionKey } from '../core/state';
import { m, type Messages } from '../i18n';
import { sfx } from '../audio/sfx';
import { say as speak, VOICES, type Voice } from '../audio/speech';
import { say } from '../ui/dialog';
import { npcName } from '../i18n/labels';
import { recalls } from './journal';
import { festival } from './festivals';
import { weatherNow } from './weather';
import { dayNumber } from './passport';
import type { Choice } from '../ui/dialog';

type TalkId = Exclude<keyof Messages['talks'], 'chat'> | keyof Messages['talks2'];

const ICON: Record<TalkId, string> = {
  emma: '👱‍♀️', rain: '👧', teacher: '👩‍🏫', chunbo: '👮', librarian: '👵', coach: '🏃', clerk: '🧑‍💼',
  grandma: '👵', bo: '👦', mom: '👩', sister: '👧', dad: '👨', xiaoyu: '🧑',
  nurse: '🩺', musicTeacher: '🎼', lifeguard: '🛟', barista: '☕', giftClerk: '🎁', deptClerk: '👔', waiter: '🍽️', stableKeeper: '🤠',
};
type TalkData = Messages['talks']['emma'];
function talkData(id: TalkId): TalkData {
  return ((m().talks as unknown as Record<string, TalkData>)[id] ?? (m().talks2 as unknown as Record<string, TalkData>)[id]);
}
// Every character speaks in their own voice: the nurse gentle and slow, the teacher calm, kids natural.
const VOICE: Partial<Record<TalkId, Voice>> = {
  mom: VOICES.mom, dad: VOICES.dad, emma: VOICES.emma, grandma: VOICES.elder, chunbo: VOICES.officer,
  teacher: VOICES.teacher, nurse: VOICES.nurse, librarian: VOICES.elder, musicTeacher: VOICES.teacher, coach: VOICES.friend,
  rain: VOICES.kid, bo: VOICES.kid, sister: { pitch: 1.5, rate: 1.05 }, xiaoyu: VOICES.xiaoyu,
  clerk: VOICES.friend, lifeguard: VOICES.friend, barista: VOICES.friend, giftClerk: VOICES.sales, deptClerk: VOICES.friend, waiter: VOICES.friend, stableKeeper: VOICES.elder,
};
const LOVE: Partial<Record<TalkId, AffectionKey>> = { mom: 'mom', dad: 'dad', sister: 'sister', emma: 'emma', chunbo: 'chunbo', xiaoyu: 'xiaoyu' };

export function hasChat(id: string): id is TalkId {
  return (id in m().talks && id !== 'chat') || id in m().talks2;
}

function greeting(id: TalkId): string {
  const g = talkData(id).greet;
  const t = S().time;
  const pool = t < 11 * 60 ? g.morning : t < 17 * 60 ? g.afternoon : g.evening;
  return pool[Math.floor(Math.random() * pool.length)];
}

/** A rotating small-talk topic; each answer gets its own reply (and a small, once-a-day effect). */
export function chatWith(id: string): void {
  if (!hasChat(id)) return;
  const s = S();
  const t = talkData(id);
  const key = `${id}.topic`;
  const n = s.npcTalk[key] ?? 0;
  s.npcTalk[key] = n + 1;
  const topics = [...t.topics, ...(m().talks3[id] ?? [])];
  const topic = topics[n % topics.length];
  const who = npcName(id);
  const react = reaction(id);
  const text = `${react ?? greeting(id)}\n${topic.say}`;
  speak(react ? `${react} ${topic.say}` : topic.say, VOICE[id]);
  say({
    who,
    icon: ICON[id],
    text,
    choices: topic.choices.map((ch, i) => ({
      text: ch.t,
      run: () => {
        sfx(i === 0 ? 'ok' : 'pop');
        if (onceToday(`chat.${id}.${n % topics.length}`)) {
          const love = LOVE[id];
          apply({ mood: i === 2 ? 4 : 2, study: i === 1 ? 1 : 0, love: love ? { [love]: i === 0 ? 3 : 1 } : undefined }, { float: false });
        }
        speak(ch.reply, VOICE[id]);
        say({ who, icon: ICON[id], text: ch.reply });
      },
    })),
  });
}

// ---------------------------------------------------------------------------
// Memory: NPCs react to what you did, to big events, and to how close you are.
// ---------------------------------------------------------------------------

/** Day numbers of the place stamps that count as trips (festivals have their own line). */
const TRIP_STAMPS = ['taipei101', 'mrt', 'museum', 'taroko', 'beach', 'jiufen', 'nightMarket'] as const;

/** Memory flags each grown-up can bring up, best first. */
const REMEMBERS: Record<string, readonly string[]> = {
  teacher: ['honest', 'calm', 'helped'],
  bo: ['stood', 'told', 'ignored'],
  mom: ['meeting'],
};

/**
 * A line reacting to the player's past choices or to a recent big event, at most
 * once a day per character; null when there's nothing to bring up.
 */
export function reaction(id: string): string | null {
  const s = S();
  const key = `cls.react.${id}`;
  if (s.daily[key]) return null;
  const r = m().classroom.react;
  let line: string | null = null;
  const flags = REMEMBERS[id] ?? [];
  const table = (r as unknown as Record<string, Record<string, string>>)[id];
  const later = dayNumber() > (s.npcTalk[`cls.mem.${id}`] ?? -1);
  const hit = later ? flags.find((f) => recalls(id, f) && !recalls(id, `said.${f}`)) : undefined;
  if (hit && table?.[hit]) {
    line = table[hit];
    // Grown-ups bring a moment up once; it stays in their memory for later.
    s.memory[id].push(`said.${hit}`);
  } else line = eventLine();
  if (line) s.daily[key] = true;
  return line;
}

/** Big recent events anyone might mention: typhoon, a trip in the last days, the festival week, rain. */
export function eventLine(): string | null {
  const s = S();
  const r = m().classroom.react;
  const w = weatherNow();
  if (w.kind === 'typhoon') return r.typhoon;
  const today = dayNumber();
  const trip = TRIP_STAMPS.find((k) => s.english.stamps[k] !== undefined && today - s.english.stamps[k] <= 2);
  if (trip && Math.random() < 0.7) return r.trip(m().passport.stampNames[trip]);
  const f = festival();
  if (f && Math.random() < 0.5) return r.festival(m().world3.fest.names[f]);
  if ((w.kind === 'rain' || w.kind === 'storm') && Math.random() < 0.5) return r.rain;
  return null;
}

export type KidId = 'xiaohua' | 'xiaomei' | 'azhe' | 'xiaoqiang' | 'xiaofang';
const KID_VOICE: Record<KidId, Voice> = { xiaohua: VOICES.xiaohua, xiaomei: VOICES.xiaomei, azhe: VOICES.azhe, xiaoqiang: VOICES.xiaoqiang, xiaofang: VOICES.xiaofang };
export const KID_ICON: Record<KidId, string> = { xiaohua: '😅', xiaomei: '📚', azhe: '😜', xiaoqiang: '⚽', xiaofang: '🎀' };

/** What each classmate remembers, most important first. `hurt` flags wait for an apology. */
const KID_FLAGS: Record<KidId, { order: readonly string[]; hurt: readonly string[] }> = {
  xiaohua: { order: ['teased', 'alarm', 'excuse'], hurt: ['teased'] },
  xiaomei: { order: ['copied', 'skipped', 'asked'], hurt: ['copied', 'skipped'] },
  azhe: { order: ['mocked', 'calm', 'clowned'], hurt: ['mocked'] },
  xiaoqiang: { order: ['sideOther', 'grabbed', 'ballOk', 'sideMe'], hurt: ['sideOther', 'grabbed'] },
  xiaofang: { order: ['sideOther', 'grabbed', 'ballOk', 'sideMe'], hurt: ['sideOther', 'grabbed'] },
};
/** react-table key for each memory flag (the ball story uses `madeUp` for a happy ending). */
const REACT_KEY: Record<string, string> = { ballOk: 'madeUp' };

/** A hurt the kid is still waiting for you to say sorry for. */
export function kidHurt(id: KidId): string | null {
  return KID_FLAGS[id].hurt.find((f) => recalls(id, f)) ?? null;
}

/**
 * How a classmate greets you: a hurt they still feel, thanks for a past kindness
 * (once), news about a big event, or a hello that depends on how close you are.
 */
export function kidGreeting(id: KidId): string {
  const s = S();
  const t = m().classroom.kids[id];
  const react = t.react as Record<string, string>;
  const hurt = kidHurt(id);
  if (hurt) return react[REACT_KEY[hurt] ?? hurt];
  // Thanks and news wait for a later day; a hurt (above) is felt straight away.
  const later = dayNumber() > (s.npcTalk[`cls.mem.${id}`] ?? -1);
  const fresh = later ? KID_FLAGS[id].order.find((f) => recalls(id, f) && !recalls(id, `said.${f}`)) : undefined;
  if (fresh) {
    s.memory[id].push(`said.${fresh}`);
    return react[REACT_KEY[fresh] ?? fresh];
  }
  if (recalls(id, 'sorry') && !recalls(id, 'said.sorry')) {
    s.memory[id].push('said.sorry');
    return t.react.madeUp;
  }
  const ev = s.daily[`cls.react.${id}`] ? null : eventLine();
  if (ev) {
    s.daily[`cls.react.${id}`] = true;
    return ev;
  }
  const love = s.affection[id];
  const ui = m().classroom.ui;
  const pool = love < 25 ? ui.coolHello : love >= 75 ? ui.warmHello : t.hello;
  return pool[Math.floor(Math.random() * pool.length)];
}

/**
 * Talk to a classmate outside their case: greeting, then a rotating topic with
 * its replies, plus `extra` choices (give back a lost thing, say sorry…).
 */
export function chatKid(id: KidId, extra: Choice[] = []): void {
  const s = S();
  const t = m().classroom.kids[id];
  const n = s.npcTalk[`${id}.topic`] ?? 0;
  const hello = kidGreeting(id);
  const who = npcName(id);
  const voice = KID_VOICE[id];
  speak(hello, voice);
  const topicChoice: Choice = {
    text: m().classroom.ui.chat,
    run: () => {
      s.npcTalk[`${id}.topic`] = n + 1;
      const topic = t.topics[n % t.topics.length];
      speak(topic.say, voice);
      say({
        who,
        icon: KID_ICON[id],
        text: topic.say,
        choices: topic.choices.map((ch, i) => ({
          text: ch.t,
          run: () => {
            sfx(i === 0 ? 'ok' : 'pop');
            if (onceToday(`chat.${id}.${n % t.topics.length}`)) apply({ mood: 2, love: { [id]: 3 } }, { float: false });
            speak(ch.reply, voice);
            say({ who, icon: KID_ICON[id], text: ch.reply });
          },
        })),
      });
    },
  };
  say({ who, icon: KID_ICON[id], text: hello, choices: [...extra, topicChoice, { text: m().classroom.ui.later }] });
}
